import { Vector3 } from 'three';
import Entity, { IEntity } from '../abstract/Entity';

export interface ISimpleMeasurement extends IEntity {
  id: number;
  startPoint: Vector3;
  endPoint: Vector3 | null;
  color: string;
  userId: string | null;
  author: string | null;
  creationDate: Date;
  modificationDate: Date;
}

class SimpleMeasurementModel extends Entity implements ISimpleMeasurement {
  id: number;

  startPoint: Vector3;

  endPoint: Vector3 | null;

  color: string;

  userId: string | null;

  author: string | null;

  creationDate: Date;

  modificationDate: Date;

  constructor(
    id: number,
    startPoint: Vector3,
    endPoint: Vector3 | null = null,
    color = '#f89306',
    userId: string | null = null,
    firstName: string = '',
    lastName: string = '',
    creationDate: Date = new Date(),
  ) {
    super();
    this.id = id;
    this.startPoint = startPoint;
    this.endPoint = endPoint;
    this.color = color;
    this.userId = userId;
    this.author = userId ? `${firstName} ${lastName}` : null;
    this.creationDate = creationDate;
    this.modificationDate = creationDate;
  }

  get length(): number | null {
    if (!this.endPoint) {
      return null;
    }
    return this.startPoint.distanceTo(this.endPoint);
  }

  /**
   * Updates the color of the measurement and sets a new modification date.
   * @param newColor - The new color for the measurement.
   */
  updateMeasurementColor(newColor: string) {
    this.color = newColor;
    this.modificationDate = new Date();
  }

  /**
   * Updates the position (startPoint and endPoint) of the measurement,
   * allowing separate updates for position only, and sets a new modification date.
   * @param newStartPoint - The new starting point for the measurement.
   * @param newEndPoint - The new ending point for the measurement.
   */
  updateMeasurementPosition(newStartPoint: Vector3, newEndPoint: Vector3 | null) {
    this.startPoint = newStartPoint;
    this.endPoint = newEndPoint;
    this.modificationDate = new Date();
  }

  getId(): number {
    return this.id;
  }

  setId(id: number): void {
    this.id = id;
  }

  getStartPoint(): Vector3 {
    return this.startPoint;
  }

  setStartPoint(startPoint: Vector3): void {
    this.startPoint = startPoint;
  }

  getEndPoint(): Vector3 | null {
    return this.endPoint;
  }

  setEndPoint(endPoint: Vector3 | null): void {
    this.endPoint = endPoint;
  }

  getColor(): string {
    return this.color;
  }

  setColor(color: string): void {
    this.color = color;
  }

  getUserId(): string | null {
    return this.userId;
  }

  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  getAuthor(): string | null {
    return this.author;
  }

  setAuthor(author: string | null): void {
    this.author = author;
  }

  getCreationDate(): Date {
    return this.creationDate;
  }

  setCreationDate(creationDate: Date): void {
    this.creationDate = creationDate;
  }

  getModificationDate(): Date {
    return this.modificationDate;
  }

  setModificationDate(modificationDate: Date): void {
    this.modificationDate = modificationDate;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      startPoint: { x: this.startPoint.x, y: this.startPoint.y, z: this.startPoint.z },
      endPoint: this.endPoint ? { x: this.endPoint.x, y: this.endPoint.y, z: this.endPoint.z } : null,
      color: this.color,
      userId: this.userId,
      author: this.author,
      creationDate: this.creationDate.toISOString(),
      modificationDate: this.modificationDate.toISOString(),
    };
  }
}

export default SimpleMeasurementModel;
